// @ts-nocheck
import React, { useState } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { useDispatch, useAppState } from '../store';
import * as api from '../api';
import type { Group, Column, Item } from '../types';
import { GROUP_COLORS } from '../types';
import ItemRow from './ItemRow';
import ColumnHeader from './ColumnHeader';
import ContextMenu, { type MenuItem } from './ContextMenu';

interface Props {
  group: Group;
  columns: Column[];
  items: Item[];
  readOnly?: boolean;
}

export default function GroupTable({ group, columns, items, readOnly }: Props) {
  const dispatch = useDispatch();
  const state = useAppState();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(group.name);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [colDragId, setColDragId] = useState<string | null>(null);
  const [colDragOverId, setColDragOverId] = useState<string | null>(null);
  const collapsed = group.collapsed === 1;

  const allSelected = items.length > 0 && selectedItems.size === items.length;
  const someSelected = selectedItems.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((i) => i.id)));
    }
  };

  const toggleSelectItem = (id: string) => {
    const next = new Set(selectedItems);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedItems(next);
  };

  const handleNameSave = async () => {
    if (!name.trim()) { setName(group.name); setEditingName(false); return; }
    await api.updateGroup(group.id, { name: name.trim() });
    dispatch({ type: 'UPDATE_GROUP', id: group.id, data: { name: name.trim() } });
    setEditingName(false);
  };

  const toggleCollapse = async () => {
    const val = collapsed ? 0 : 1;
    dispatch({ type: 'UPDATE_GROUP', id: group.id, data: { collapsed: val } });
    await api.updateGroup(group.id, { collapsed: val });
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) return;
    const maxPos = items.length > 0 ? Math.max(...items.map((i) => i.position)) : 0;
    const item = await api.createItem(group.id, group.board_id, newItemName.trim(), maxPos + 1000);
    dispatch({ type: 'ADD_ITEM', item });
    setNewItemName('');
  };

  const handleDeleteGroup = async () => {
    if (!confirm(`Delete group "${group.name}" and all its items?`)) return;
    await api.deleteGroup(group.id);
    dispatch({ type: 'REMOVE_GROUP', id: group.id });
  };

  const handleColorChange = async (color: string) => {
    dispatch({ type: 'UPDATE_GROUP', id: group.id, data: { color } });
    await api.updateGroup(group.id, { color });
    setShowColorPicker(false);
  };

  // Bulk actions
  const handleDeleteSelected = async () => {
    if (!confirm(`Delete ${selectedItems.size} selected item(s)?`)) return;
    for (const id of selectedItems) {
      await api.deleteItem(id);
      dispatch({ type: 'REMOVE_ITEM', id });
    }
    setSelectedItems(new Set());
  };

  const handleDuplicateSelected = async () => {
    for (const id of selectedItems) {
      const dup = await api.duplicateItem(id);
      dispatch({ type: 'ADD_ITEM', item: dup });
      const itemCells = state.cells.filter((c) => c.item_id === id);
      for (const cell of itemCells) {
        dispatch({ type: 'SET_CELL', item_id: dup.id, column_id: cell.column_id, value: cell.value });
      }
    }
    setSelectedItems(new Set());
  };

  const handleMoveSelectedToGroup = async (targetGroupId: string) => {
    const targetItems = state.items.filter((i) => i.group_id === targetGroupId);
    let maxPos = targetItems.length > 0 ? Math.max(...targetItems.map((i) => i.position)) : 0;
    for (const id of selectedItems) {
      maxPos += 1000;
      await api.updateItem(id, { group_id: targetGroupId, position: maxPos });
      dispatch({ type: 'UPDATE_ITEM', id, data: { group_id: targetGroupId, position: maxPos } });
    }
    setSelectedItems(new Set());
  };

  // Column drag-and-drop handlers
  const handleColDragStart = (e: React.DragEvent, columnId: string) => {
    setColDragId(columnId);
    e.dataTransfer.setData('columnId', columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColDragOver = (e: React.DragEvent, columnId: string) => {
    if (colDragId && colDragId !== columnId) {
      setColDragOverId(columnId);
    }
  };

  const handleColDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('columnId') || colDragId;
    if (!sourceId || sourceId === targetColumnId) {
      setColDragId(null);
      setColDragOverId(null);
      return;
    }
    const sorted = [...columns].sort((a, b) => a.position - b.position);
    const sourceIdx = sorted.findIndex((c) => c.id === sourceId);
    const targetIdx = sorted.findIndex((c) => c.id === targetColumnId);
    if (sourceIdx === -1 || targetIdx === -1) return;

    // Calculate new position
    let newPos: number;
    const filtered = sorted.filter((c) => c.id !== sourceId);
    const adjustedTargetIdx = targetIdx > sourceIdx ? targetIdx - 1 : targetIdx;
    if (filtered.length === 0) {
      newPos = 1000;
    } else if (adjustedTargetIdx <= 0) {
      newPos = filtered[0].position - 1000;
    } else if (adjustedTargetIdx >= filtered.length) {
      newPos = filtered[filtered.length - 1].position + 1000;
    } else {
      newPos = (filtered[adjustedTargetIdx - 1].position + filtered[adjustedTargetIdx].position) / 2;
    }

    dispatch({ type: 'UPDATE_COLUMN', id: sourceId, data: { position: newPos } });
    await api.updateColumn(sourceId, { position: newPos });
    setColDragId(null);
    setColDragOverId(null);
  };

  const handleColDragEnd = () => {
    setColDragId(null);
    setColDragOverId(null);
  };

  const contextMenuItems: MenuItem[] = [
    { label: 'Rename', onClick: () => { setName(group.name); setEditingName(true); } },
    { label: 'Change color', onClick: () => setShowColorPicker(true) },
    { label: collapsed ? 'Expand' : 'Collapse', onClick: toggleCollapse },
    { divider: true, label: '' },
    { label: 'Delete group', danger: true, onClick: handleDeleteGroup },
  ];

  const otherGroups = state.groups.filter((g) => g.id !== group.id);

  return (
    <div className="group">
      <div
        className="group-header"
        onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
      >
        <button
          className={`group-collapse-btn ${collapsed ? 'collapsed' : ''}`}
          onClick={toggleCollapse}
          style={{ color: group.color }}
        >
          ▼
        </button>

        {editingName ? (
          <input
            className="group-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
            style={{ color: group.color }}
          />
        ) : (
          <span
            className="group-name"
            style={{ color: group.color, cursor: 'pointer' }}
            onClick={() => { setName(group.name); setEditingName(true); }}
          >
            {group.name}
          </span>
        )}

        <span className="group-item-count">{items.length} items</span>

        <button
          className="group-menu-btn"
          onClick={(e) => setCtxMenu({ x: e.clientX, y: e.clientY })}
        >
          ⋯
        </button>

        {showColorPicker && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9989 }} onClick={() => setShowColorPicker(false)} />
            <div className="color-picker" style={{ top: 32, left: 40 }}>
              {GROUP_COLORS.map((c) => (
                <div
                  key={c}
                  className={`color-swatch ${c === group.color ? 'active' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => handleColorChange(c)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {ctxMenu && (
        <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={contextMenuItems} onClose={() => setCtxMenu(null)} />
      )}

      {/* Bulk action bar */}
      {someSelected && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
          background: '#6161ff', borderRadius: 8, margin: '4px 0', color: 'white', fontSize: 13,
        }}>
          <span style={{ fontWeight: 600 }}>{selectedItems.size} selected</span>
          <button
            onClick={handleDuplicateSelected}
            style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 13 }}
          >
            Duplicate
          </button>
          {otherGroups.length > 0 && (
            <div style={{ position: 'relative' }}>
              <select
                onChange={(e) => { if (e.target.value) handleMoveSelectedToGroup(e.target.value); }}
                defaultValue=""
                style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '4px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 13 }}
              >
                <option value="" disabled>Move to...</option>
                {otherGroups.map((g) => (
                  <option key={g.id} value={g.id} style={{ color: '#323338' }}>{g.name}</option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={handleDeleteSelected}
            style={{ background: 'rgba(226,68,92,0.8)', color: 'white', padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 13, marginLeft: 'auto' }}
          >
            Delete
          </button>
          <button
            onClick={() => setSelectedItems(new Set())}
            style={{ background: 'none', color: 'rgba(255,255,255,0.7)', padding: '4px 8px', border: 'none', cursor: 'pointer', fontSize: 16 }}
          >
            ✕
          </button>
        </div>
      )}

      {!collapsed && (
        <table className="board-table">
          <thead>
            <tr>
              <th style={{ borderLeft: `4px solid ${group.color}`, width: 40, minWidth: 40 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleSelectAll}
                  style={{ width: 16, height: 16, accentColor: '#6161ff', cursor: 'pointer' }}
                />
              </th>
              <th>Item</th>
              {columns.map((col) => (
                <ColumnHeader
                  key={col.id}
                  column={col}
                  onDragStart={handleColDragStart}
                  onDragOver={handleColDragOver}
                  onDrop={handleColDrop}
                  onDragEnd={handleColDragEnd}
                  dragOverId={colDragOverId}
                />
              ))}
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <Droppable droppableId={group.id} type="ITEM">
            {(provided, snapshot) => (
              <tbody
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{ background: snapshot.isDraggingOver ? '#f5f6ff' : undefined }}
              >
                {items.map((item, index) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    columns={columns}
                    groupColor={group.color}
                    selected={selectedItems.has(item.id)}
                    onToggleSelect={() => toggleSelectItem(item.id)}
                  />
                ))}
                {provided.placeholder}
                {!readOnly && (
                <tr className="add-item-row">
                  <td style={{ borderLeft: `4px solid ${group.color}` }}></td>
                  <td colSpan={columns.length + 2}>
                    {addingItem ? (
                      <input
                        className="add-item-input"
                        autoFocus
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { handleAddItem(); }
                          if (e.key === 'Escape') { setAddingItem(false); setNewItemName(''); }
                        }}
                        onBlur={() => { if (newItemName.trim()) handleAddItem(); setAddingItem(false); setNewItemName(''); }}
                        placeholder="+ Add item"
                      />
                    ) : (
                      <button className="add-item-btn" onClick={() => setAddingItem(true)}>
                        + Add item
                      </button>
                    )}
                  </td>
                </tr>
                )}
              </tbody>
            )}
          </Droppable>
        </table>
      )}
    </div>
  );
}
